import { deviceStorage } from '../platform/web'
import type { Lang } from '../i18n/langs'

/**
 * Регион хранения данных: на какой сервер ходит это устройство.
 *
 * По 152-ФЗ данные жителей России должны с первой записи ложиться в базу на территории
 * России, поэтому у Moonwake два независимых сервера: российский — только API, для
 * остальных — казахстанский, он же отдаёт сайт (решение заказчика и юриста 19.10.2026,
 * `docs/legal-review.md` §4.1). Серверы друг о друге не знают и ничего не синхронизируют.
 *
 * Страну человек называет сам до первого запроса с данными — перед полем почты. Подсказка
 * в списке — по языку интерфейса, не по адресу сети: геолокацию по IP решили не заводить.
 * Выбор окончательный: переключателя региона в приложении нет. Единственный способ,
 * которым регион устройства может смениться, — ссылка входа из письма другого сервера,
 * а она приходит только тому, у кого там уже есть аккаунт.
 *
 * Хранится в общем ключе устройства `api_base` полным адресом: его же читает оболочка
 * для магазина (`ServerSync.kt`), и ей ничего объяснять не надо. Пусто — казахстанский
 * сервер того же домена, как было всегда.
 */
export type Region = 'world' | 'ru'

export const REGIONS: readonly Region[] = ['ru', 'world']

/** Адрес API по региону. Пустой путь — тот же домен, что и сайт: без CORS и второго сертификата. */
const BASES: Record<Region, string> = {
  world: '/api',
  ru: 'https://api-ru.moonwake.online/api',
}

/** Адрес API для заданного региона — нужен переносу: он обращается к новому серверу до того, как переключит регион устройства. */
export function baseFor(r: Region): string {
  return BASES[r]
}

const KEY = 'api_base'

let текущий: string | null = null
let загружен = false

/** Прочитать выбор с устройства. Зовётся при запуске до первого обращения к серверу. */
export async function loadRegion(): Promise<void> {
  const stored = await deviceStorage().get(KEY)
  текущий = stored === null || stored.trim() === '' ? null : stored.trim()
  загружен = true
}

/** Базовый адрес API для всех запросов. До загрузки — казахстанский: иного и не бывало. */
export function apiBase(): string {
  return текущий ?? BASES.world
}

/** Регион устройства; null — ещё не выбран (данных на сервере нет, спрашивать можно). */
export function region(): Region | null {
  if (!загружен) return null
  if (текущий === null) return null
  return текущий === BASES.ru ? 'ru' : 'world'
}

/**
 * Запомнить регион. Другой сервер — другой ключ устройства: прежний там неизвестен и
 * отвечал бы 401, поэтому его забываем, новый выдаст сервер региона при первом обращении.
 */
export async function setRegion(next: Region): Promise<void> {
  const base = BASES[next]
  const сменился = текущий !== null && текущий !== base
  // Казахстанский регион — отсутствие ключа: так оболочка берёт свой адрес по умолчанию,
  // а устройства, которые ничего не выбирали, остаются где были
  if (next === 'world') await deviceStorage().remove(KEY)
  else await deviceStorage().set(KEY, base)
  текущий = next === 'world' ? null : base
  загружен = true
  if (сменился) {
    try {
      globalThis.localStorage?.removeItem('moonwake.device_token')
    } catch {
      // приватный режим — ключа и не было
    }
  }
}

/**
 * Что предложить в списке, пока человек не выбрал: по языку интерфейса. Русскому
 * интерфейсу не подсказываем ничего — на нём сидят и Россия, и Казахстан, и угадывать
 * здесь нельзя: это выбор страны хранения данных. Остальным языкам Россия маловероятна.
 */
export function suggestedRegion(lang: Lang): Region | null {
  // Предвыбор по часовому поясу устройства, а не по языку: русский интерфейс и у Казахстана,
  // а российские зоны почти наверняка Россия (разумная мера по 152-ФЗ, юрист 19.10.2026).
  // GeoIP не заводим. При расхождении tz и выбора экран показывает мягкую подсказку.
  if (российскийПояс()) return 'ru'
  return lang === 'ru' ? null : 'world'
}

/** Часовой пояс устройства — из российских зон IANA. Без сети и без запроса адреса. */
export function российскийПояс(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
    return RU_ZONES.has(tz)
  } catch {
    return false
  }
}

/** Зоны IANA России (Europe/* и Asia/*). Список закрытый — меняется крайне редко. */
const RU_ZONES = new Set([
  'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Simferopol', 'Europe/Kirov', 'Europe/Volgograd',
  'Europe/Astrakhan', 'Europe/Saratov', 'Europe/Ulyanovsk', 'Europe/Samara',
  'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Novosibirsk', 'Asia/Barnaul', 'Asia/Tomsk',
  'Asia/Novokuznetsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Chita', 'Asia/Yakutsk',
  'Asia/Khandyga', 'Asia/Vladivostok', 'Asia/Ust-Nera', 'Asia/Magadan', 'Asia/Sakhalin',
  'Asia/Srednekolymsk', 'Asia/Kamchatka', 'Asia/Anadyr',
])
